import React, { useEffect } from 'react';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const Section = ({ number, title, children }) => (
  <section className="mt-10">
    <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
      {number}. {title}
    </h2>
    <div className="text-white/70 leading-relaxed space-y-3">{children}</div>
  </section>
);

const TermsOfServicePage = () => {
  useEffect(() => {
    document.title = 'Terms of Service · AutoPostr';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <article className="max-w-3xl mx-auto" data-testid="terms-content">
          <header className="mb-10">
            <p className="text-sm text-white/50 uppercase tracking-wider mb-3">Legal</p>
            <h1 className="text-3xl md:text-5xl font-bold mb-3">Terms of Service</h1>
            <p className="text-white/60 text-sm">
              Effective date: <span className="text-white/80">1 September 2026</span>
            </p>
          </header>

          <p className="text-white/70 leading-relaxed">
            These Terms of Service (the &ldquo;Terms&rdquo;) govern your access to and use of
            AutoPostr (the &ldquo;Service&rdquo;), a social-media management and content
            publishing platform. Please read these Terms carefully before using the Service.
          </p>

          <Section number={1} title="Acceptance of Terms">
            <p>
              By creating an AutoPostr account, accessing the Service, or otherwise using
              AutoPostr, you agree to be bound by these Terms. If you do not agree with any
              part of these Terms, you must not use the Service.
            </p>
          </Section>

          <Section number={2} title="Description of the Service">
            <p>AutoPostr provides tools that may include:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Connections to supported social-media accounts</li>
              <li>Content creation and content uploading</li>
              <li>Content scheduling</li>
              <li>Automated publishing to supported platforms</li>
              <li>Social-media account management</li>
              <li>Supported analytics and reporting features, where available</li>
            </ul>
            <p>
              Features of the Service may be added, modified, suspended or discontinued at
              any time where reasonably necessary.
            </p>
          </Section>

          <Section number={3} title="User Accounts">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>You must provide accurate and up-to-date information when registering.</li>
              <li>You are responsible for maintaining the security of your account credentials.</li>
              <li>You are responsible for all activity that occurs through your account.</li>
              <li>You must notify AutoPostr promptly at{' '}
                <a href="mailto:support@autopostr.co.uk" className="text-emerald-400 hover:text-emerald-300 underline">
                  support@autopostr.co.uk
                </a>{' '}
                if you become aware of any unauthorised access to your account.
              </li>
            </ul>
          </Section>

          <Section number={4} title="Connected Social-Media Accounts">
            <p>
              You are responsible for ensuring that you have the necessary permission to
              connect and manage any Facebook, Instagram, Threads or other social-media
              accounts you link to AutoPostr.
            </p>
            <p>
              AutoPostr uses official platform APIs and integrations where available. Your
              use of connected social-media accounts must comply with the terms, policies
              and community standards of the relevant platform.
            </p>
            <p>
              AutoPostr cannot guarantee that any third-party platform will remain
              available, or that its APIs, permissions, policies or functionality will not
              change.
            </p>
          </Section>

          <Section number={5} title="User Content">
            <p>
              You retain ownership of the content you upload to or create through AutoPostr
              (&ldquo;User Content&rdquo;).
            </p>
            <p>
              You grant AutoPostr the limited rights necessary to host, process, transmit,
              schedule and publish your User Content for the purpose of providing the
              Service you have requested.
            </p>
            <p>
              You are solely responsible for ensuring that you have all rights, licences and
              permissions required to upload and publish any User Content.
            </p>
          </Section>

          <Section number={6} title="Prohibited Use">
            <p>You must not use AutoPostr to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Break the law or facilitate illegal activity</li>
              <li>Infringe the intellectual-property rights of any person</li>
              <li>Distribute malware, viruses or malicious code</li>
              <li>Attempt unauthorised access to systems, accounts or data</li>
              <li>Abuse, overload or disrupt the Service</li>
              <li>Conduct fraud or facilitate fraudulent activity</li>
              <li>Send spam to users or connected platforms</li>
              <li>Circumvent restrictions imposed by any social-media platform</li>
              <li>
                Violate the policies of Meta, Facebook, Instagram, Threads or any other
                connected platform
              </li>
              <li>Upload unlawful, harmful, harassing or otherwise objectionable content</li>
            </ul>
          </Section>

          <Section number={7} title="Automated Publishing">
            <p>
              Scheduled and automated publishing relies on third-party APIs and services.
              AutoPostr does not guarantee that every scheduled post will publish
              successfully. Publishing may be affected by, without limitation:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Third-party API outages or degradations</li>
              <li>Platform restrictions or policy changes</li>
              <li>Expired, revoked or invalid access tokens</li>
              <li>Restrictions applied to your connected account</li>
              <li>Invalid, rejected or non-compliant content</li>
              <li>Rate limits imposed by the platform</li>
              <li>Technical failures beyond our reasonable control</li>
            </ul>
          </Section>

          <Section number={8} title="Third-Party Services">
            <p>
              AutoPostr integrates with third-party services and social-media platforms.
              Your use of such services is governed by the terms and policies of each
              third-party provider.
            </p>
            <p>
              AutoPostr is not responsible for changes, restrictions or failures caused by
              third-party services.
            </p>
          </Section>

          <Section number={9} title="Fees and Subscriptions">
            <p>
              Some parts of AutoPostr may be offered on a paid basis. Where you subscribe to
              a paid plan, you agree to pay the fees and comply with the billing terms
              presented at the point of purchase.
            </p>
            <p>
              Cancellation, refund and billing-cycle details, where applicable, will be
              provided at the point of subscription and in your account settings.
            </p>
          </Section>

          <Section number={10} title="Suspension and Termination">
            <p>
              AutoPostr may suspend or terminate your account where reasonably necessary,
              including in the event of:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>A violation of these Terms</li>
              <li>Illegal activity</li>
              <li>Abuse of the Service or of other users</li>
              <li>Security concerns</li>
              <li>Suspected fraud</li>
              <li>Attempts to circumvent restrictions</li>
            </ul>
            <p>
              You may stop using the Service at any time and request account deletion in
              accordance with our{' '}
              <a href="/data-deletion" className="text-emerald-400 hover:text-emerald-300 underline">
                Data Deletion
              </a>{' '}
              page.
            </p>
          </Section>

          <Section number={11} title="Data and Privacy">
            <p>
              Our collection and processing of personal data is governed by our{' '}
              <a href="/privacy-policy" className="text-emerald-400 hover:text-emerald-300 underline">
                Privacy Policy
              </a>. By using AutoPostr you acknowledge that you have read and understood
              the Privacy Policy.
            </p>
          </Section>

          <Section number={12} title="User Data Deletion">
            <p>
              You may request deletion of your AutoPostr account and associated personal
              information at any time via our{' '}
              <a href="/data-deletion" className="text-emerald-400 hover:text-emerald-300 underline">
                Data Deletion
              </a>{' '}
              page, subject to applicable legal obligations and legitimate retention
              requirements.
            </p>
          </Section>

          <Section number={13} title="Intellectual Property">
            <p>
              AutoPostr&rsquo;s software, branding, design, user interface and underlying
              technology are owned by AutoPostr or its licensors. Except for the limited
              right to use the Service granted to you during your active subscription or
              permitted access period, no rights are transferred to you.
            </p>
          </Section>

          <Section number={14} title="Warranties and Service Availability">
            <p>
              AutoPostr aims to provide a reliable service, but does not warrant that the
              Service will be uninterrupted, error-free, or available at all times. The
              Service is provided on an &ldquo;as available&rdquo; basis, save for
              warranties that cannot be excluded under applicable law.
            </p>
          </Section>

          <Section number={15} title="Limitation of Liability">
            <p>
              To the fullest extent permitted by applicable law, AutoPostr shall not be
              liable for any indirect, incidental, consequential, special or punitive
              damages, or for any loss of profits, revenue, data or goodwill, arising out of
              or in connection with your use of the Service.
            </p>
            <p>
              Nothing in these Terms limits or excludes any liability that cannot be limited
              or excluded under applicable law, including liability for death or personal
              injury caused by negligence, or for fraud or fraudulent misrepresentation.
            </p>
          </Section>

          <Section number={16} title="Indemnity">
            <p>
              To the extent permitted by law, you agree to indemnify and hold harmless
              AutoPostr from and against any claims, losses, liabilities and reasonable
              costs arising from: (a) your unlawful use of the Service; (b) your
              infringement of any third-party right, including intellectual-property or
              privacy rights; or (c) your breach of these Terms.
            </p>
          </Section>

          <Section number={17} title="Changes to the Service">
            <p>
              AutoPostr may modify, update or discontinue features of the Service where
              reasonably necessary. Where a change materially affects your use of a paid
              subscription, we will use reasonable efforts to notify you.
            </p>
          </Section>

          <Section number={18} title="Changes to These Terms">
            <p>
              AutoPostr may update these Terms from time to time. The most current version
              will always be available at{' '}
              <a href="https://autopostr.co.uk/terms" className="text-emerald-400 hover:text-emerald-300 underline">
                https://autopostr.co.uk/terms
              </a>. Where changes are material, we will use reasonable efforts to notify
              you. Continued use of the Service after the effective date of updated Terms
              constitutes acceptance of the changes.
            </p>
          </Section>

          <Section number={19} title="Governing Law">
            <p>
              These Terms and any dispute or claim arising out of or in connection with
              them shall be governed by the laws of the jurisdiction in which AutoPostr
              operates, without regard to conflict-of-law principles. Nothing in these
              Terms affects any statutory rights that you may have as a consumer under the
              laws of your country of residence.
            </p>
          </Section>

          <Section number={20} title="Contact">
            <p>
              For questions about these Terms, please contact us at{' '}
              <a
                href="mailto:support@autopostr.co.uk"
                data-testid="terms-contact-email"
                className="text-emerald-400 hover:text-emerald-300 underline"
              >
                support@autopostr.co.uk
              </a>.
            </p>
          </Section>

          <hr className="border-white/10 my-12" />
          <p className="text-white/40 text-sm">
            See also our{' '}
            <a href="/privacy-policy" className="text-emerald-400 hover:text-emerald-300 underline">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/data-deletion" className="text-emerald-400 hover:text-emerald-300 underline">
              Data Deletion Instructions
            </a>.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
